import i18n from '@/services/localization';
import { getDaysInCurrentMonth } from './utils';

export const PackageTypes = {
  CUSTOM: 'CUSTOM',
  MAIN: 'MAIN',
  FREE: 'FREE',
};

export enum SUBSCRIPTION_METHOD {
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export const getSubscriptionType = (): Record<string, string> => {
  return {
    MONTH: i18n.t('pricing.monthly'),
    YEAR: i18n.t('pricing.annually'),
  };
};

export const SUBSCRIPTION_TYPE2: Record<string, string> = {
  MONTH: 'Monthly',
  YEAR: 'Annual',
  ONETIME: 'Onetime',
  TWO_YEARS: '2 Years',
};

export const SIDEMENU_NAVIGATION = {
  PROJECTS: 'projects',
  USERS: 'users',
  BILLING: 'billing',
  PRICING: 'pricing',
  ADMINS: 'admins',
  VENDORS: 'vendors',
  DEALS: 'deals',
  STATS: 'stats',
  SETTINGS: 'settings',
  DOCS: 'docs',
  VERSIONS: 'package-versions',
  LEADS: 'leads',
  TRUSTED_SOURCES: 'trusted-sources',
  ENTERPRISES: 'enterprises',
  A11Y_SCANNER: 'a11y-scanner',
};

export type PagesDict = Record<string, Record<string, string>>;

export const PAGES: PagesDict = {
  BEFORE_LOGIN: {
    MAIN_PAGE: '/home',
    ADMIN_SIGN_IN: '/admin',
    VENDOR_SIGN_IN: '/vendor',
    SIGN_IN: '/sign-in',
    SIGN_IN_INVITE: '/sign-in/invite',
    PRICING: '/pricing',
    OUR_TEAM: '/our-team',
    FAQ: '/faq',
    ABOUT_US: '/about-us',
    WHITEPAPERS: '/whitepapers',
    PASSWORD: '/password',
    SECURITY_ALERT: '/security-alert',
    MAIN_LANDING_PAGE: '/lp/home/v1',
    EAA_LANDING_PAGE: '/lp/eaa/v1',
    ENTERPRISE_LANDING_PAGE: '/lp/enterprise/v1',
    COMPLIANCE: '/compliance',
    WEB_PLATFORMS: '/web-platforms',
    MOBILE_PLATFORMS: '/mobile-platforms',
    COLOR_RATIO_CHECKER: '/color-ratio-checker',
    COLOR_CONTRAST_CHECKER: '/color-contrast-checker',
    FONT_CHECKER: '/font-checker',
    A11Y_STATEMENT_GENERATOR: '/a11y-statement-generator',
    ACCESSIBILITY_CHECKER: '/accessibility-checker',
    ALTERNATIVES: '/alternatives',
  },
  AFTER_LOGIN: {
    PAYMENT: '/payment',
    PRICING: `/${SIDEMENU_NAVIGATION.PRICING}`,
    CHECKOUT: `/${SIDEMENU_NAVIGATION.PRICING}/checkout`,
    NEW_PROJECT: `/${SIDEMENU_NAVIGATION.PROJECTS}/new-project`,
    USERS: `/${SIDEMENU_NAVIGATION.USERS}`,
    BILLING: `/${SIDEMENU_NAVIGATION.BILLING}`,
    PROJECTS: `/${SIDEMENU_NAVIGATION.PROJECTS}`,
    UPGRADE: `/${SIDEMENU_NAVIGATION.BILLING}/upgrade`,
    CONFIGURATION: `/${SIDEMENU_NAVIGATION.PROJECTS}/:projectIdParam/configuration`,
    DOCS: `/${SIDEMENU_NAVIGATION.PROJECTS}/:projectIdParam/docs`,
    ADMINS: `/${SIDEMENU_NAVIGATION.ADMINS}`,
    VENDORS: `/${SIDEMENU_NAVIGATION.VENDORS}`,
    DEALS: `/${SIDEMENU_NAVIGATION.DEALS}`,
    STATS: `/${SIDEMENU_NAVIGATION.STATS}`,
    SETTINGS: `/${SIDEMENU_NAVIGATION.SETTINGS}`,
    ADMIN_DOCS: `/${SIDEMENU_NAVIGATION.DOCS}`,
    ADMIN_DOCUMENTATION: `/${SIDEMENU_NAVIGATION.DOCS}/:projectIdParam/documentation`,
    ADMIN_CONFIGURATION: `/${SIDEMENU_NAVIGATION.DOCS}/:projectIdParam/configuration`,
    ADMIN_VERSIONS: `/${SIDEMENU_NAVIGATION.VERSIONS}`,
    ADMIN_BDR: `/${SIDEMENU_NAVIGATION.LEADS}`,
    ADMIN_TRUSTED_SOURCES: `/${SIDEMENU_NAVIGATION.TRUSTED_SOURCES}`,
    ADMIN_ENTERPRISES: `/${SIDEMENU_NAVIGATION.ENTERPRISES}`,
    ADMIN_A11Y_SCANNER: `/${SIDEMENU_NAVIGATION.A11Y_SCANNER}`,
  },
  SHARED: {
    DEFAULT: '/',
    REDIRECT: '/sso/:provider/redirect',
    TERMS_AND_CONDITIONS: `/terms-and-conditions`,
    PRIVACY_POLICY: `/privacy-policy`,
    ACCESSIBILITY_STATEMENT: `/accessibility-statement`,
    EULA: `/eula`,
    BLOG: `blog`,
    CAREERS: `careers`,
    NOT_FOUND: '*',
  },
};

export const LINKS = {
  TERMS: '/terms-and-conditions',
};

export type JourneyType = {
  CAMPAIGN: string;
  MEDIUM: string;
  SOURCE: string;
  TERM: string;
  CONTENT: string;
  ID: string;
};

export type CookiesType = {
  JOURNEY: string;
  ACCESS_TOKEN: string;
  REDIRECT_URL: string;
  THEME: string;
};

export const COOKIES: CookiesType = {
  ACCESS_TOKEN: 'accessToken',
  REDIRECT_URL: 'urlToRedirect',
  THEME: 'theme',
  JOURNEY: 'userJourney',
};

export enum AccountType {
  GOOGLE,
  MICROSOFT,
  BITBUCKET,
  GITHUB,
  LINEKDIN,
}

export enum USER_TYPE {
  ADMIN = 'ADMIN',
  CLIENT = 'CLIENT',
  VENDOR = 'VENDOR',
}

export enum UserRole {
  ADMIN = 'ADMIN',
  USER = 'USER',
  ENTERPRISE = 'ENTERPRISE',
}

export enum ModificationsCode {
  FRAMEWORKS = 'FRAMEWORKS',
  DOMAINS = 'DOMAINS',
  TRAINING = 'TRAINING_AND_AUDITING_20H',
  USERS = 'USERS',
  ACCESSIBILITY_COMPONENTS = 'ACCESSIBILITY_COMPONENTS',
  PAYMENT_INTERVAL = 'PAYMENT_INTERVAL',
  ADDITIONAL_AUDITING_SERVICE = 'ADDITIONAL_AUDITING_SERVICE',
}

export const LOCALES = {
  DAY_MONTH_YEAR: 'en-GB',
  MONTH_DAY_YEAR: 'default',
  EN_US: 'en-US',
};

export const NUM_OF_DAYS: Record<string, number> = {
  ONE_DAY: 1,
  TWO_WEEKS: 14,
  YEAR: 365,
  MONTH: getDaysInCurrentMonth(),
};

export const CACHE_TTL_SECONDS = {
  DEFAULT: 1800,
};

export const TIME: Record<string, number> = {
  DAYS_IN_WEEK: 7,
  HOURS_IN_DAY: 24,
  MINUTES_IN_HOUR: 60,
  MILLI_IN_SECOND: 1000,
};

export const RES_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  UNPROCESSABLE: 422,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONTENT_TOO_LARGE: 413,
};

export const FRAMEWORK_TITLES: Record<string, string> = {
  REACT: 'React',
  VUE_JS: 'VueJS',
  VUEJS: 'VueJS',
  ANGULAR: 'Angular',
  JAVASCRIPT: 'JavaScript',
  ANDROID_XML_SDK: 'Android/XML',
  IOS_SDK: 'iOS SDK',
  REACT_NATIVE_SDK: 'React Native SDK',
  FLUTTER_SDK: 'Flutter SDK',
};

export enum FRAMEWORKS_ENUM {
  // Web
  REACT = 'REACT',
  VUEJS = 'VUEJS',
  ANGULAR = 'ANGULAR',
  JAVASCRIPT = 'JAVASCRIPT',

  // Mobile
  ANDROID_XML_SDK = 'ANDROID_XML_SDK',
  IOS_SDK = 'IOS_SDK',
  REACT_NATIVE_SDK = 'REACT_NATIVE_SDK',
  FLUTTER_SDK = 'FLUTTER_SDK',
}

export enum FRAMEWORK_PLATFORMS {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
}

export enum PLATFORM_PREFIXES {
  U1 = 'U1',
  M1 = 'M1',
  C1 = 'C1',
}

export enum ProjectCredentialType {
  BASIC = 'BASIC',
  TOKEN = 'TOKEN',
}

export enum ProjectCredentialAccessType {
  RUNTIME = 'RUNTIME',
  INSTALLATION = 'INSTALLATION',
}

export const FrameworkName: Record<string, string> = {
  REACT: 'ReactJS',
  VUEJS: 'VueJS',
  ANGULAR: 'Angular',
  JAVASCRIPT: 'JavaScript',
  ANDROID_XML_SDK: 'Android XML SDK',
  FLUTTER_SDK: 'Flutter',
  IOS_SDK: 'iOS SDK',
  REACT_NATIVE_SDK: 'React Native SDK',
};

export enum THEME_ICONS {
  WHITE = 'white',
  DARK = 'dark',
}

export const COOKIE_LIMIT = 4096;
